import React, { useEffect, useMemo, useState } from "react";
import { accessApi } from "../../services/accessApi";

const EMPTY_FORM = {
  subject_type: "hr_role",
  subject_key: "",
  module_code: "",
  scope: "self",
  is_active: 1,
  reason: "",
};

function Badge({ children, tone = "gray" }) {
  const tones = {
    green:
      "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900",
    red:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900",
    gray:
      "bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:border-neutral-700",
    blue:
      "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900",
  };

  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export default function ITScopeRulesPage() {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const [subjectTypeFilter, setSubjectTypeFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError("");

      const [rulesRes, modulesRes] = await Promise.all([
        accessApi.getScopeRules(),
        accessApi.getModules(),
      ]);

      setRows(rulesRes?.data?.data || []);
      setModules(modulesRes?.data?.data || []);
    } catch (err) {
      console.error("ITScopeRulesPage load error:", err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load scope rules."
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return rows.filter((r) => {
      const matchesQ = !needle
        ? true
        : [r.subject_type, r.subject_key, r.module_code, r.scope]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle);

      const matchesSubjectType = !subjectTypeFilter
        ? true
        : r.subject_type === subjectTypeFilter;

      const matchesModule = !moduleFilter
        ? true
        : r.module_code === moduleFilter;

      const matchesScope = !scopeFilter ? true : r.scope === scopeFilter;

      const matchesStatus =
        statusFilter === ""
          ? true
          : Number(r.is_active) === Number(statusFilter);

      return (
        matchesQ &&
        matchesSubjectType &&
        matchesModule &&
        matchesScope &&
        matchesStatus
      );
    });
  }, [rows, q, subjectTypeFilter, moduleFilter, scopeFilter, statusFilter]);

  const openCreate = () => {
    setEditingRow(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setForm({
      subject_type: row.subject_type || "hr_role",
      subject_key: row.subject_key || "",
      module_code: row.module_code || "",
      scope: row.scope || "self",
      is_active: Number(row.is_active) === 1 ? 1 : 0,
      reason: "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingRow(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = {
        subject_type: form.subject_type,
        subject_key: form.subject_key?.trim(),
        module_code: form.module_code,
        scope: form.scope,
        is_active: Number(form.is_active) === 1 ? 1 : 0,
        reason: form.reason?.trim() || null,
      };

      if (!payload.subject_key || !payload.module_code || !payload.scope) {
        alert("Subject key, module and scope are required.");
        return;
      }

      if (editingRow?.id) {
        await accessApi.updateScopeRule(editingRow.id, payload);
      } else {
        await accessApi.createScopeRule(payload);
      }

      closeModal();
      await load();
    } catch (err) {
      console.error("submit scope rule error:", err);
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to save scope rule."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    try {
      await accessApi.updateScopeRule(row.id, {
        subject_type: row.subject_type,
        subject_key: row.subject_key,
        module_code: row.module_code,
        scope: row.scope,
        is_active: Number(row.is_active) === 1 ? 0 : 1,
        reason:
          Number(row.is_active) === 1
            ? "Deactivated from Scope Rules screen"
            : "Activated from Scope Rules screen",
      });

      await load();
    } catch (err) {
      console.error("toggle scope status error:", err);
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to update scope status."
      );
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this scope rule?")) return;

    try {
      await accessApi.deleteScopeRule(id);
      await load();
    } catch (err) {
      console.error("delete scope rule error:", err);
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to delete scope rule."
      );
    }
  };

  if (loading) {
    return <div className="p-6 text-black dark:text-white">Loading scope rules...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">
            Scope Rules
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Define visibility scope by role, policy or user.
          </p>
        </div>

        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium"
        >
          + Add Rule
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search scope rules..."
            className="rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          />

          <select
            value={subjectTypeFilter}
            onChange={(e) => setSubjectTypeFilter(e.target.value)}
            className="rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">All subject types</option>
            <option value="hr_role">hr_role</option>
            <option value="policy">policy</option>
            <option value="user">user</option>
          </select>

          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">All modules</option>
            {modules.map((m) => (
              <option key={m.code} value={m.code}>
                {m.code}
              </option>
            ))}
          </select>

          <select
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
            className="rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">All scopes</option>
            <option value="none">none</option>
            <option value="self">self</option>
            <option value="subtree">subtree</option>
            <option value="department">department</option>
            <option value="full">full</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">All statuses</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Subject Type
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Subject Key
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Module
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Scope
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Status
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition"
                >
                  <td className="p-3 text-black dark:text-white">
                    {row.subject_type}
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {row.subject_key}
                  </td>
                  <td className="p-3">
                    <Badge tone="blue">{row.module_code}</Badge>
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {row.scope}
                  </td>
                  <td className="p-3">
                    {Number(row.is_active) === 1 ? (
                      <Badge tone="green">Active</Badge>
                    ) : (
                      <Badge tone="red">Inactive</Badge>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openEdit(row)}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-700"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => toggleStatus(row)}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-700"
                      >
                        {Number(row.is_active) === 1 ? "Deactivate" : "Activate"}
                      </button>

                      <button
                        onClick={() => remove(row.id)}
                        className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 bg-red-50 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    No scope rules found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-black dark:text-white">
                {editingRow ? "Edit Scope Rule" : "Add Scope Rule"}
              </h2>
              <button
                onClick={closeModal}
                className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
              >
                Close
              </button>
            </div>

            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Subject Type
                </label>
                <select
                  value={form.subject_type}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, subject_type: e.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                >
                  <option value="hr_role">hr_role</option>
                  <option value="policy">policy</option>
                  <option value="user">user</option>
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Subject Key
                </label>
                <input
                  value={form.subject_key}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, subject_key: e.target.value }))
                  }
                  placeholder="e.g. admin / policy_ar_user / 123"
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Module
                </label>
                <select
                  value={form.module_code}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, module_code: e.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                >
                  <option value="">Select module...</option>
                  {modules.map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.code}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Scope
                </label>
                <select
                  value={form.scope}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, scope: e.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                >
                  <option value="none">none</option>
                  <option value="self">self</option>
                  <option value="subtree">subtree</option>
                  <option value="department">department</option>
                  <option value="full">full</option>
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Status
                </label>
                <select
                  value={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_active: Number(e.target.value),
                    }))
                  }
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Reason
                </label>
                <input
                  value={form.reason}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  placeholder="Optional audit reason"
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                />
              </div>

              <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}